import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from '../utilities/chunk-load-error.util';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (
      isChunkLoadError(error) ||
      /Loading chunk .+ failed/i.test(
        error?.message ?? error.toString?.() ?? '',
      )
    ) {
      if (reloadOnceForChunkError()) {
        return;
      }
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="font-mono"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '20px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          }}
        >
          <div
            style={{
              maxWidth: '800px',
              width: '100%',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow:
                '0 4px 6px rgba(0, 0, 0, 0.07), 0 10px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '32px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                }}
              >
                ⚠️
              </div>
              <h1
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '28px',
                  fontWeight: '600',
                }}
              >
                Oops! Something went wrong
              </h1>
              <p style={{ margin: 0, fontSize: '16px', opacity: 0.95 }}>
                We encountered an unexpected error. Don't worry, your data is
                safe.
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '32px' }}>
              {/* Quick Actions */}
              <div
                style={{
                  marginBottom: '24px',
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    flex: '1',
                    minWidth: '140px',
                    padding: '12px 24px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#5568d3';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 8px rgba(102, 126, 234, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 4px rgba(102, 126, 234, 0.2)';
                  }}
                >
                  🔄 Reload Page
                </button>
                <button
                  onClick={() => window.history.back()}
                  style={{
                    flex: '1',
                    minWidth: '140px',
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7f8ff';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Go Back
                </button>
              </div>

              {/* Error Details Accordion */}
              <details
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  overflow: 'hidden',
                }}
              >
                <summary
                  style={{
                    padding: '16px 20px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    color: '#495057',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '18px' }}>🔍</span>
                  <span>View Technical Details</span>
                </summary>
                <div style={{ padding: '0 20px 20px' }}>
                  {/* Error Message */}
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '8px',
                      }}
                    >
                      Error Message
                    </div>
                    <div
                      style={{
                        backgroundColor: '#fff5f5',
                        border: '1px solid #feb2b2',
                        borderRadius: '6px',
                        padding: '12px',
                        color: '#c53030',
                        fontFamily:
                          'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        overflowX: 'auto',
                      }}
                    >
                      {this.state.error?.toString()}
                    </div>
                  </div>

                  {/* Stack Trace */}
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '8px',
                      }}
                    >
                      Stack Trace
                    </div>
                    <pre
                      style={{
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        borderRadius: '6px',
                        padding: '16px',
                        margin: 0,
                        fontFamily:
                          'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        overflowX: 'auto',
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {this.state.error?.stack}
                    </pre>
                  </div>

                  {/* Component Stack */}
                  {this.state.errorInfo && (
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#6c757d',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '8px',
                        }}
                      >
                        Component Stack
                      </div>
                      <pre
                        style={{
                          backgroundColor: '#1e1e1e',
                          color: '#d4d4d4',
                          borderRadius: '6px',
                          padding: '16px',
                          margin: 0,
                          fontFamily:
                            'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '12px',
                          lineHeight: '1.6',
                          overflowX: 'auto',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>

              {/* Help Text */}
              <div
                style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: '#f0f4ff',
                  border: '1px solid #d0d9ff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#4c5980',
                  lineHeight: '1.6',
                }}
              >
                <strong style={{ display: 'block', marginBottom: '8px' }}>
                  💡 What you can do:
                </strong>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Try reloading the page</li>
                  <li>Go back to the previous page</li>
                  <li>Clear your browser cache</li>
                  <li>
                    If the problem persists, contact support with the error
                    details above
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
